import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import MainNav from "@/components/MainNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { createCheckoutSession, calculateShipping } from "@/lib/api";
import { FaTrash, FaShoppingBag, FaArrowLeft, FaLock } from "react-icons/fa";

const COUNTRIES = [
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" },
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "IE", name: "Ireland" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "JP", name: "Japan" },
  { code: "NZ", name: "New Zealand" },
  { code: "CH", name: "Switzerland" },
  { code: "AT", name: "Austria" },
  { code: "PT", name: "Portugal" },
  { code: "PL", name: "Poland" },
];

export default function CartPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [shippingCost, setShippingCost] = useState(0);
  const [loading, setLoading] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: "",
    email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    postal_code: "",
    country: ""
  });

  useEffect(() => {
    loadCart();
    window.addEventListener("cartUpdated", loadCart);
    return () => window.removeEventListener("cartUpdated", loadCart);
  }, []);

  const loadCart = () => {
    const savedCart = JSON.parse(localStorage.getItem("cart") || "[]");
    setCart(savedCart);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(price);
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const total = subtotal + shippingCost;

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const newCart = [...cart];
    newCart[index].quantity = newQuantity;
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
  };

  const removeItem = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    toast.success("Item removed");
  };

  const handleCountryChange = async (country) => {
    setShippingAddress({ ...shippingAddress, country });
    try {
      const res = await calculateShipping(country);
      setShippingCost(res.data.shipping_cost);
    } catch (error) {
      console.error("Error calculating shipping:", error);
    }
  };

  const handleCheckout = async () => {
    // Validate form
    if (!shippingAddress.name || !shippingAddress.email || !shippingAddress.address_line1 || 
        !shippingAddress.city || !shippingAddress.postal_code || !shippingAddress.country) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!shippingAddress.email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      const items = cart.map(item => ({
        product_id: item.product_id,
        size: item.size,
        quantity: item.quantity
      }));

      const res = await createCheckoutSession(items, shippingAddress, window.location.origin);
      
      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background" data-testid="cart-page">
        <MainNav />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <FaShoppingBag className="w-20 h-20 mx-auto text-zinc-700 mb-6" />
            <h1 className="text-2xl font-syne font-bold text-white mb-4">Your cart is empty</h1>
            <p className="text-zinc-500 mb-8">Add some items to get started.</p>
            <Link to="/shop">
              <Button className="bg-white text-black hover:bg-zinc-200 rounded-full px-8">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="cart-page">
      <MainNav />

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <button
            onClick={() => navigate("/shop")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          >
            <FaArrowLeft /> Continue Shopping
          </button>

          <h1 className="text-3xl font-syne font-bold text-white mb-8">Your Cart</h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, index) => (
                <motion.div
                  key={`${item.product_id}-${item.size}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex gap-4"
                  data-testid={`cart-item-${index}`}
                >
                  <div className="w-24 h-24 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-contain p-2" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-600">
                        <FaShoppingBag />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-white">{item.name}</h3>
                    <p className="text-sm text-zinc-500">Size: {item.size}</p>
                    <p className="text-zinc-300 mt-1">{formatPrice(item.price)}</p>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button
                      onClick={() => removeItem(index)}
                      className="text-zinc-500 hover:text-red-500 transition-colors"
                    >
                      <FaTrash />
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(index, item.quantity - 1)}
                        className="w-8 h-8 rounded border border-zinc-700 text-zinc-400 hover:border-zinc-500"
                      >
                        -
                      </button>
                      <span className="text-white w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(index, item.quantity + 1)}
                        className="w-8 h-8 rounded border border-zinc-700 text-zinc-400 hover:border-zinc-500"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Checkout Form */}
            <div className="lg:col-span-1">
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 sticky top-24">
                <h2 className="font-syne font-bold text-white mb-6">Shipping Details</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-zinc-400">Full Name *</Label>
                    <Input
                      value={shippingAddress.name}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Email *</Label>
                    <Input
                      type="email"
                      value={shippingAddress.email}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, email: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Country *</Label>
                    <Select onValueChange={handleCountryChange} value={shippingAddress.country}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        {COUNTRIES.map(country => (
                          <SelectItem key={country.code} value={country.code} className="text-white hover:bg-zinc-800">
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Address *</Label>
                    <Input
                      value={shippingAddress.address_line1}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, address_line1: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Address Line 2</Label>
                    <Input
                      value={shippingAddress.address_line2}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, address_line2: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="Apt 4B"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">City *</Label>
                      <Input
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Postal Code *</Label>
                      <Input
                        value={shippingAddress.postal_code}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, postal_code: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="border-t border-zinc-800 mt-6 pt-6 space-y-3">
                  <div className="flex justify-between text-zinc-400">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-400">
                    <span>Shipping</span>
                    <span>{shippingAddress.country ? formatPrice(shippingCost) : "Select country"}</span>
                  </div>
                  <div className="flex justify-between text-white text-lg font-bold pt-3 border-t border-zinc-800">
                    <span>Total</span>
                    <span>{formatPrice(total)}</span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={loading || !shippingAddress.country}
                  className="w-full mt-6 py-6 bg-white text-black hover:bg-zinc-200 rounded-full font-medium"
                  data-testid="checkout-btn"
                >
                  {loading ? (
                    "Processing..."
                  ) : (
                    <>
                      <FaLock className="mr-2" /> Secure Checkout
                    </>
                  )}
                </Button>

                <p className="text-xs text-zinc-500 text-center mt-4">
                  Powered by Stripe. Your payment info is secure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
